import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getTimezoneAbbreviation, prepareDateRangeForQuery } from '@/utils/date-range';
import { formatDuration, formatTimestampShort } from '@/utils/format-call';
import { trpc } from '@/utils/trpc';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import {
  ChevronLeft,
  ChevronRight,
  Filter as FilterIcon,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
} from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { z } from 'zod';

type PaginatedCallsQueryOutput = Omit<RouterOutput['calls']['getPaginatedCalls'], 'summaryStats'>;
type CallItemType = PaginatedCallsQueryOutput['items'][number];
type CallSummaryStatsOutput = RouterOutput['calls']['getCallSummaryStats'];

const CALL_TYPES = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'PATIENT', label: 'Patient' },
  { value: 'PROVIDER', label: 'Provider' },
  { value: 'OTHER', label: 'Other' },
] as const;

const DISCONNECTION_REASONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'user_busy', label: 'User Busy' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'failed', label: 'Failed' },
  { value: 'agent_error', label: 'Agent Error' },
] as const;

const CALL_DIRECTIONS = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
] as const;

const callsIndexSearchSchema = z.object({
  page: z.int().min(1).catch(1),
  startDate: z.iso.datetime({ offset: true }).optional(),
  endDate: z.iso.datetime({ offset: true }).optional(),
  status: z.string().optional(),
  direction: z.string().optional(),
  columns: z.string().optional(),
  defaultTags: z.string().optional(),
  transferDestination: z.string().optional(),
  fromNumber: z.string().optional(),
  toNumber: z.string().optional(),
});

export const Route = createFileRoute('/_protected/calls/')({
  component: CallsListComponent,
  validateSearch: callsIndexSearchSchema,
});

const PAGE_SIZE = 10;

const TABLE_COLUMNS = [
  { id: 'time', label: 'Time', defaultVisible: true, className: 'w-[180px]' },
  { id: 'callDuration', label: 'Duration', defaultVisible: true, className: 'w-[100px]' },
  { id: 'direction', label: 'Direction', defaultVisible: false, className: 'w-[100px]' },
  { id: 'from', label: 'From', defaultVisible: true, className: 'w-[150px]' },
  { id: 'to', label: 'To', defaultVisible: true, className: 'w-[150px]' },
  { id: 'callStatus', label: 'Status', defaultVisible: false, className: 'w-[120px]' },
  {
    id: 'transferDestination',
    label: 'Transfer Dest.',
    defaultVisible: false,
    className: 'w-[150px]',
  },
  { id: 'isResolved', label: 'Resolved', defaultVisible: false, className: 'w-[100px]' },
  { id: 'defaultTags', label: 'Default Tags', defaultVisible: true, className: 'w-[200px]' },
  { id: 'callId', label: 'Call ID', defaultVisible: false, className: 'w-[200px]' },
] as const;

type ColumnId = (typeof TABLE_COLUMNS)[number]['id'];

// Allowed column IDs
const ALLOWED_COLUMN_IDS = TABLE_COLUMNS.map((c) => c.id);

// New component for displaying summary statistics
function CallSummaryStatsComponent({
  startDate,
  endDate,
  callerType,
  disconnectionReasonSearch,
  direction,
  defaultTags,
  transferDestination,
  fromNumber,
  toNumber,
  includeOther,
}: {
  startDate?: string;
  endDate?: string;
  callerType?: string;
  disconnectionReasonSearch?: string;
  direction?: string;
  defaultTags?: string[];
  transferDestination?: string[];
  fromNumber?: string;
  toNumber?: string[];
  includeOther?: boolean;
}) {
  const {
    data: summaryStats,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = trpc.calls.getCallSummaryStats.useQuery(
    {
      startDate,
      endDate,
      callerType: callerType || undefined,
      disconnectionReason: disconnectionReasonSearch || undefined,
      direction: direction || undefined,
      defaultTags: defaultTags || undefined,
      transferDestination: transferDestination || undefined,
      fromNumber: fromNumber || undefined,
      toNumber: toNumber && toNumber.length > 0 ? toNumber : undefined,
      includeOther: includeOther || undefined,
    },
    { placeholderData: (previousData: CallSummaryStatsOutput | undefined) => previousData }
  );

  const displayStats = summaryStats ?? {
    all: 0,
    transferred: 0,
    resolved: 0,
    failed: 0,
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">All</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {(isLoadingSummary || isFetchingSummary) && !displayStats.all ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              displayStats.all
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Transferred</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {(isLoadingSummary || isFetchingSummary) && !displayStats.transferred ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              displayStats.transferred
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Trapeze Resolved</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {(isLoadingSummary || isFetchingSummary) && !displayStats.resolved ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              displayStats.resolved
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CallsListComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  const {
    page,
    startDate,
    endDate,
    direction,
    columns,
    defaultTags,
    transferDestination,
    fromNumber,
    toNumber,
  } = searchParams;

  // Parse comma-separated strings back to arrays - memoized to prevent infinite loops
  const defaultTagsArray = React.useMemo(
    () => (defaultTags ? defaultTags.split(',').filter(Boolean) : []),
    [defaultTags]
  );
  const transferDestinationArray = React.useMemo(
    () => (transferDestination ? transferDestination.split(',').filter(Boolean) : []),
    [transferDestination]
  );
  const toNumberArray = React.useMemo(
    () => (toNumber ? toNumber.split(',').filter(Boolean) : []),
    [toNumber]
  );

  const timezoneAbbr = getTimezoneAbbreviation();

  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange | undefined>(() => {
    if (startDate && endDate) {
      try {
        const fromDate = new Date(startDate);
        const toDate = new Date(endDate);
        if (!Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime())) {
          return { from: fromDate, to: toDate };
        }
      } catch (error) {
        console.warn('Error parsing date range from URL search params:', error);
      }
    }
    // Default to past week if no valid dates in URL
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 7);
    return { from: fromDate, to: toDate };
  });

  // Set initial URL params if not present
  React.useEffect(() => {
    if (!startDate || !endDate) {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 7);

      navigate({
        search: (prev) => ({
          ...prev,
          startDate: fromDate.toISOString(),
          endDate: toDate.toISOString(),
        }),
      });
    }
  }, [startDate, endDate, navigate]);

  const [visibleColumns, setVisibleColumns] = React.useState<Set<ColumnId>>(
    () => new Set(TABLE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id))
  );

  const [filterInputs, setFilterInputs] = React.useState<{
    direction: string;
    defaultTags: string[];
    transferDestination: string[];
    fromNumber: string;
    toNumber: string[];
  }>({
    direction: searchParams.direction || '',
    defaultTags: defaultTagsArray,
    transferDestination: transferDestinationArray,
    fromNumber: fromNumber || '',
    toNumber: toNumberArray,
  });

  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    setFilterInputs({
      direction: searchParams.direction || '',
      defaultTags: defaultTagsArray,
      transferDestination: transferDestinationArray,
      fromNumber: fromNumber || '',
      toNumber: toNumberArray,
    });
  }, [
    searchParams.direction,
    defaultTagsArray,
    transferDestinationArray,
    fromNumber,
    toNumberArray,
  ]);

  // On mount, set visibleColumns from URL if present
  React.useEffect(() => {
    if (columns) {
      const colSet = new Set(
        columns
          .split(',')
          .filter((id): id is ColumnId => ALLOWED_COLUMN_IDS.includes(id as ColumnId))
      );
      setVisibleColumns(colSet);
    }
  }, [columns]);

  // When visibleColumns changes, update the URL
  React.useEffect(() => {
    const colString = Array.from(visibleColumns).join(',');
    if (colString !== (columns || '')) {
      navigate({
        search: (prev) => ({
          ...prev,
          columns: colString,
        }),
      });
    }
  }, [visibleColumns, columns, navigate]);

  // Fetch available filter options
  const { data: availableDefaultTags = [] } = trpc.calls.getAvailableDefaultTags.useQuery();
  const { data: availableTransferDestinations = [] } =
    trpc.calls.getAvailableTransferDestinations.useQuery();
  const { data: toNumbersData } = trpc.calls.getAvailableToNumbers.useQuery();

  // Build filter options with "Other" bucket support for low-volume phone numbers
  const OTHER_BUCKET_VALUE = '__OTHER__';
  const toNumberFilterOptions = React.useMemo(() => {
    if (!toNumbersData) return [];
    const options: { value: string; label: string }[] = toNumbersData.phoneNumbers.map(
      (phoneNumber) => ({ value: phoneNumber, label: phoneNumber })
    );
    if (toNumbersData.hasOther) {
      options.push({ value: OTHER_BUCKET_VALUE, label: 'Other' });
    }
    return options;
  }, [toNumbersData]);

  // Derive effective toNumbers and includeOther flag from URL params (not local state)
  // This ensures filters only apply after clicking "Apply", consistent with other filters
  const { effectiveToNumbers, includeOther } = React.useMemo(() => {
    if (toNumberArray.length === 0) return { effectiveToNumbers: [], includeOther: false };
    const hasOther = toNumberArray.includes(OTHER_BUCKET_VALUE);
    const explicitNumbers = toNumberArray.filter((v) => v !== OTHER_BUCKET_VALUE);
    return { effectiveToNumbers: explicitNumbers, includeOther: hasOther };
  }, [toNumberArray]);

  const queryResult = trpc.calls.getPaginatedCalls.useQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      startDate,
      endDate,
      direction: searchParams.direction || undefined,
      defaultTags: defaultTagsArray.length > 0 ? defaultTagsArray : undefined,
      transferDestination:
        transferDestinationArray.length > 0 ? transferDestinationArray : undefined,
      fromNumber: fromNumber || undefined,
      // Pass array to backend to support multi-select filtering with "Other" bucket
      toNumber: effectiveToNumbers.length > 0 ? effectiveToNumbers : undefined,
      includeOther: includeOther || undefined,
    },
    {
      placeholderData: (previousData: PaginatedCallsQueryOutput | undefined) => previousData,
    }
  );

  const { data, isLoading, isError, error, isFetching } = queryResult;

  const handlePreviousPage = () => {
    if (page > 1) {
      navigate({
        search: (prev) => ({
          ...prev,
          page: page - 1,
        }),
      });
    }
  };

  const handleNextPage = () => {
    if (data?.totalPages && (data.currentPage ?? page) < data.totalPages) {
      navigate({
        search: (prev) => ({
          ...prev,
          page: (data.currentPage ?? page) + 1,
        }),
      });
    }
  };

  const handleDateRangeSelect = (dateRange: DateRange | undefined) => {
    setSelectedDateRange(dateRange);

    // Use shared utility to convert date range to API format
    // This ensures consistent date range handling across all pages
    const { startDate: isoStartDate, endDate: isoEndDate } = prepareDateRangeForQuery(
      dateRange?.from,
      dateRange?.to
    );

    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        startDate: isoStartDate,
        endDate: isoEndDate,
      }),
    });
  };

  const handleApplyFilters = () => {
    setFilterDropdownOpen(false);
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        direction: filterInputs.direction || undefined,
        defaultTags:
          filterInputs.defaultTags.length > 0 ? filterInputs.defaultTags.join(',') : undefined,
        transferDestination:
          filterInputs.transferDestination.length > 0
            ? filterInputs.transferDestination.join(',')
            : undefined,
        fromNumber: filterInputs.fromNumber || undefined,
        toNumber: filterInputs.toNumber.length > 0 ? filterInputs.toNumber.join(',') : undefined,
      }),
    });
  };

  const handleClearFilters = () => {
    setFilterDropdownOpen(false);
    setFilterInputs({
      direction: '',
      defaultTags: [],
      transferDestination: [],
      fromNumber: '',
      toNumber: [],
    });
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        direction: undefined,
        defaultTags: undefined,
        transferDestination: undefined,
        fromNumber: undefined,
        toNumber: undefined,
      }),
    });
  };

  if (isLoading && !data) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2">Loading call history...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 md:p-6 lg:p-8 text-red-500">
        Error fetching call history: {error ? error.message : 'Unknown error'}
      </div>
    );
  }

  const callsToDisplay = data?.items || [];

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Conversation History</h1>
      </div>

      <CallSummaryStatsComponent
        startDate={startDate}
        endDate={endDate}
        callerType={undefined}
        disconnectionReasonSearch={undefined}
        direction={searchParams.direction}
        defaultTags={defaultTagsArray}
        transferDestination={transferDestinationArray}
        fromNumber={fromNumber}
        toNumber={effectiveToNumbers}
        includeOther={includeOther}
      />

      <div className="space-y-2">
        <span className="text-xs text-muted-foreground">Times shown in {timezoneAbbr}</span>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker dateRange={selectedDateRange} setDateRange={handleDateRangeSelect} />

          <DropdownMenu open={filterDropdownOpen} onOpenChange={setFilterDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                {isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FilterIcon className="mr-2 h-4 w-4" />
                )}
                Filters
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80 p-4 space-y-4" align="start">
              <DropdownMenuLabel>Filter by</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <div className="space-y-2">
                <Label className="text-sm font-medium">From Number</Label>
                <Input
                  type="text"
                  placeholder="Enter phone number..."
                  value={filterInputs.fromNumber}
                  onChange={(e) =>
                    setFilterInputs((prev) => ({ ...prev, fromNumber: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">To Number</Label>
                  <MultiSelectFilter
                    label="To Number"
                    options={toNumberFilterOptions}
                    selectedValues={filterInputs.toNumber}
                    onSelectionChange={(values) =>
                      setFilterInputs((prev) => ({ ...prev, toNumber: values }))
                    }
                    placeholder="Select numbers..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Direction</Label>
                <Select
                  value={filterInputs.direction}
                  onValueChange={(value: string) =>
                    setFilterInputs((prev) => ({ ...prev, direction: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_DIRECTIONS.map((direction) => (
                      <SelectItem key={direction.value} value={direction.value}>
                        {direction.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Default Tags</Label>
                  <MultiSelectFilter
                    label="Default Tags"
                    options={availableDefaultTags}
                    selectedValues={filterInputs.defaultTags}
                    onSelectionChange={(values) =>
                      setFilterInputs((prev) => ({ ...prev, defaultTags: values }))
                    }
                    placeholder="Select tags..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Transfer Destination</Label>
                  <MultiSelectFilter
                    label="Transfer Destination"
                    options={availableTransferDestinations}
                    selectedValues={filterInputs.transferDestination}
                    onSelectionChange={(values) =>
                      setFilterInputs((prev) => ({ ...prev, transferDestination: values }))
                    }
                    placeholder="Select destinations..."
                  />
                </div>
              </div>

              <DropdownMenuSeparator />
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={isFetching}
                >
                  Clear
                </Button>
                <Button size="sm" onClick={handleApplyFilters} disabled={isFetching}>
                  Apply
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                {/* Consider adding an icon like <Settings className="mr-2 h-4 w-4" /> */}
                Customize Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {TABLE_COLUMNS.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={visibleColumns.has(column.id)}
                  onCheckedChange={(checked: boolean) => {
                    setVisibleColumns((prev) => {
                      const next = new Set(prev);
                      if (checked) {
                        next.add(column.id);
                      } else {
                        next.delete(column.id);
                      }
                      return next;
                    });
                  }}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-grow" />
        </div>
      </div>

      {isFetching && (!data || !data.items || data.items.length === 0) && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 p-2 bg-blue-500 text-white text-center text-sm rounded-md shadow-lg">
          Updating data...
        </div>
      )}

      <div className="border rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {TABLE_COLUMNS.map(
                (column) =>
                  visibleColumns.has(column.id) && (
                    <TableHead key={column.id} className={column.className}>
                      {column.label}
                    </TableHead>
                  )
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {callsToDisplay.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.size || 1} className="h-24 text-center">
                  No call records found.
                </TableCell>
              </TableRow>
            ) : (
              callsToDisplay.map((call: CallItemType) => (
                <Link
                  key={call.id}
                  to="/calls/$callId"
                  params={{ callId: call.id }}
                  search={(prev) => ({
                    ...prev,
                    page: data?.currentPage ?? page,
                    startDate,
                    endDate,
                  })}
                  style={{ display: 'contents' }}
                >
                  <TableRow className="cursor-pointer hover:bg-muted/50">
                    {visibleColumns.has('time') && (
                      <TableCell>{formatTimestampShort(call.timestamp)}</TableCell>
                    )}
                    {visibleColumns.has('callDuration') && (
                      <TableCell>{formatDuration(call.durationMs)}</TableCell>
                    )}
                    {visibleColumns.has('direction') && (
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {call.type === 'inbound' ? (
                            <PhoneIncoming className="mr-1.5 h-3.5 w-3.5" />
                          ) : (
                            <PhoneOutgoing className="mr-1.5 w-3.5" />
                          )}
                          {call.type}
                        </Badge>
                      </TableCell>
                    )}
                    {visibleColumns.has('from') && (
                      <TableCell>{call.callerNumber || 'N/A'}</TableCell>
                    )}
                    {visibleColumns.has('to') && <TableCell>{call.toNumber || 'N/A'}</TableCell>}
                    {visibleColumns.has('callStatus') && (
                      <TableCell>
                        <Badge
                          variant={
                            call.status === 'completed'
                              ? 'outline'
                              : call.status === 'in_progress'
                                ? 'default'
                                : 'secondary'
                          }
                          className="capitalize"
                        >
                          {call.status || 'N/A'}
                        </Badge>
                      </TableCell>
                    )}
                    {visibleColumns.has('transferDestination') && (
                      <TableCell>
                        {call.callAnalysisStatus === 'COMPLETED' ? (
                          call.transferDestination || 'N/A'
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.has('isResolved') && (
                      <TableCell>
                        {call.callAnalysisStatus === 'COMPLETED' ? (
                          <Badge variant={call.isResolved ? 'default' : 'secondary'}>
                            {call.isResolved ? 'Yes' : 'No'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.has('defaultTags') && (
                      <TableCell>
                        {call.callAnalysisStatus === 'COMPLETED' ? (
                          Array.isArray(call.defaultTags) && call.defaultTags.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-center">
                              {call.defaultTags.slice(0, 2).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {call.defaultTags.length > 2 && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  +{call.defaultTags.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            'N/A'
                          )
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.has('callId') && (
                      <TableCell className="font-mono text-xs">
                        {call.retellCallId || call.id}
                      </TableCell>
                    )}
                  </TableRow>
                </Link>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data?.totalPages && data.totalPages > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            Page {data.currentPage ?? page} of {data.totalPages} (Total: {data.totalItems ?? 0}{' '}
            calls)
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={(data.currentPage ?? page) <= 1 || isFetching}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={(data.currentPage ?? page) >= data.totalPages || isFetching}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
